import { Basemaps, Condition, Input } from "./enums";

export interface IConfig {
    default: IDefault;
    basemaps: (IBasemapTileXYZ | IBasemapTileWMS)[];
    layers: ILayerConfig[];
    geoLocation?: ILocation;
    forms: IForms;
    search: ISearch;
    reCaptchaSiteKey: string;
    reCaptchaErrorMessage: string;
    upload: IUploadConfig;
    print: IPrintConfig;
    email: IEmailConfig;
    geometryService: IGeometryServiceConfig;
}
export interface IDefault {
    center: number[];
    extentSize: number[];
    viewExtent: number[];
    zoom: number;
    minZoom: number;
    maxZoom: number;
    projection: string;
    zoomToLevel: number;
    clusterLayerId: string;
    polygonLayerId: string;
    highLightLayerId: string;
    drawingLayerId: string;
    tileGrid: ITileGrid;
}
export interface ITileGrid {
    origin: number[];
    tileSize?: number[];
    resolutions: number[];
}
export interface IBasemapTileXYZ {
    id: string;
    title: string;
    type: string;
    visible: boolean;
    source: {
        url: string;
        maxZoom: number;
        projection: string;
        crossOrigin: string;
    };
}
export interface IBasemapTileWMS {
    id: string;
    title: string;
    type: string;
    visible: boolean;
    source: {
        url: string;
        params?: ILayerParams;
        projection: string;
        serverType?: string;
        crossOrigin: string;
    };
}
export interface ILayerConfig {
    id: string;
    title: string;
    type: string;
    copyright: string;
    url: string;
    crossOrigin: string;
    visible: boolean;
    showInToc: boolean;
    zIndex: number;
    minResolution?: number;
    maxResolution?: number;
    extent?: number[];
    params?: ILayerParams;
    legend?: ILegend[];
}

export interface ILegend {
    title: string;
    fillColor?: string;
    borderColor?: string;
}
export interface ILayerParams {
    LAYERS?: string;
    FORMAT?: string;
    transparent?: boolean;
}
export interface ILocation {
    geolocationOptions?: {
        maximumAge: number;
        timeout: number;
        enableHighAccuracy: boolean;
    };
}

export interface IIdentify {
    fields: string[];
    conditionalFields: IIdentifyCondition[];
}

export interface IIdentifyCondition {
    field: string;
    value: string | number | boolean;
    condition: Condition;
    showFields: string[];
}

export interface IForms {
    textFilter: IInputFieldConfig;
    individualApplicantForm: IFormConfig;
    companyApplicantForm: IFormConfig;
    eventForm: IFormConfig;
}

export interface IFormConfig {
    fields: IInputFieldConfig[];
}
export interface IInputFieldConfig {
    field: string;
    title: string;
    type?: Input;
    autocomplete?: string;
    required?: boolean;
    validationFn?: (value, comparator?: string) => boolean;
    validationErrorMessage?: string;
    validationComparator?: string;
    visibilityDeterminantField?: string;
    visibilityDeterminantFieldValue?: string | number | boolean;
    requiredDeterminantField?: string;
    requiredDeterminantFieldValue?: string | number | boolean;
    info?: string;
    minDateField?: string;
    maxDateField?: string;
    minDate?: string;
    maxDate?: string;
    hasError?: boolean;
    maxlength?: number; // max length for Text or TextArea fields
}
export interface ISearch {
    services: ISearchService[];
}
export interface ISearchService {
    id: string;
    url: string;
    searchLayers: ISearchLayer[];
}

export interface ISearchLayer {
    layerId: string;
    field: string;
}

export interface IUploadConfig {
    url: string;
    fieldName: string;
    layerName: string;
}

export interface IPrintConfig {
    endpoint: string;
    url: string;
}

export interface IFileProviderConfig {
    fieldName: string;
    layerName: string;
    endpoint: string;
}

export interface IEmailConfig {
    subject: string;
    body: string;
    url: string;
}
export interface IGeometryServiceConfig {
    endpoint: string;
    url: string;
}

export const proxyUrl = process.env.NODE_ENV === "development" ? "https://lvmkartes.lvm.lv/pubevents/beta/" : "";
export const all = "Izvēlēties vērtību";
export const defaultOption = { name: all, code: undefined };

export const config: IConfig = {
    default: {
        center: [530000, 310000],
        extentSize: [530000, 310000],
        viewExtent: [160000, 40000, 900000, 570000],
        zoom: 4.5,
        minZoom: 3.5,
        maxZoom: 20,
        projection: "EPSG:3059",
        zoomToLevel: 13,
        clusterLayerId: "clusterLayer",
        polygonLayerId: "polygonLayer",
        highLightLayerId: "highlightLayer",
        drawingLayerId: "drawingLayer",
        tileGrid: {
            origin: [-5120900, 3998100],
            tileSize: [512, 512],
            resolutions: [
                1058.3354500042335, 529.1677250021168, 264.5838625010584, 132.2919312505292, 52.91677250021167,
                26.458386250105836, 13.229193125052918, 10.583354500042335, 7.9375158750317505, 5.291677250021167,
                3.9687579375158752, 2.6458386250105836, 1.3229193125052918, 0.5291677250021167
            ]
        }
    },
    basemaps: [
        {
            id: Basemaps.Mamma,
            title: "LVM Fona karte",
            type: "TileXYZ",
            visible: true,
            source: {
                url: proxyUrl + "proxy/D341478CE74F4F02B68607991448D499/CloudMap_mammadaba/MapServer",
                maxZoom: 14,
                projection: "EPSG:3059",
                crossOrigin: "anonymous"
            }
        },
        {
            id: Basemaps.Orto,
            title: "Ortofoto karte",
            type: "TileWMS",
            visible: false,
            source: {
                url: "https://lvmgeoserver.lvm.lv/geoserver/ows",
                params: {
                    LAYERS: "public:Orto_LKS",
                    FORMAT: "image/vnd.jpeg-png8",
                    transparent: false
                },
                projection: "EPSG:3059",
                serverType: "geoserver",
                crossOrigin: "anonymous"
            }
        }
    ],
    layers: [
        {
            id: "zemesaprobezojumi",
            title: "Zemes aprobežojumi",
            type: "ImageArcGISRest",
            copyright: "LVM",
            url: proxyUrl + "proxy/D341478CE74F4F02B68607991448D499/LVMpublicForm/NIvLandRestrictions/MapServer",
            crossOrigin: "anonymous",
            visible: true,
            showInToc: true,
            zIndex: 10,
            legend: [
                {
                    title: "Darbība jāsaskaņo ar DAP",
                    fillColor: "#e280ff"
                },
                {
                    title: "Darbība aizliegta visu gadu",
                    fillColor: "#ff8080"
                },
                {
                    title: "Darbība aizliegta no 1.marta līdz 31.augustam",
                    fillColor: "#80f3d4"
                }
            ]
        },
        {
            id: "cadaster",
            title: "Kadastra karte",
            type: "ImageArcGISRest",
            copyright: "Valsts Zemes dienests",
            url: proxyUrl + "proxy/7cb29dcbb61442b7a22c289b22d671b8/MapServer",
            crossOrigin: "anonymous",
            maxResolution: 20,
            params: { LAYERS: "show:2,4,17,20" },
            visible: true,
            showInToc: true,
            zIndex: 11
        },
        {
            id: "roadsLVM",
            title: "LVM autoceļi",
            type: "ImageArcGISRest",
            copyright: "LVM",
            url: proxyUrl + "proxy/D341478CE74F4F02B68607991448D499/LVMLV/LITEpubdataLVM/MapServer",
            crossOrigin: "anonymous",
            params: { LAYERS: "show:13" },
            visible: true,
            showInToc: true,
            zIndex: 12
        }
    ],
    geoLocation: {
        geolocationOptions: {
            maximumAge: 0,
            timeout: 15000,
            enableHighAccuracy: true
        }
    },
    forms: {
        textFilter: {
            field: "TEXT-FILTER",
            title: "Teksta meklēšana"
        },
        individualApplicantForm: {
            fields: [
                { field: "APPLICANTNAME", title: "Vārds, uzvārds", type: Input.Text, required: true },
                {
                    field: "APPLICANTADDRESS",
                    title: "Adrese",
                    type: Input.Text,
                    autocomplete: "street-address",
                    required: true
                },
                {
                    field: "APPLICANTPHONE",
                    title: "Telefons",
                    type: Input.Phone,
                    autocomplete: "tel",
                    required: true,
                    validationFn: (value): boolean => (value.match(/^\+?\d{8,11}$/) ? true : false), // only digits, 8-11 symbols
                    validationErrorMessage: "Kļūdains telefona numurs"
                },
                {
                    field: "APPLICANTEMAIL",
                    title: "E-pasts",
                    type: Input.Email,
                    autocomplete: "email",
                    required: true,
                    validationFn: (value): boolean =>
                        value.match(/^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,6})+$/) ? true : false,
                    validationErrorMessage: "Kļūdains epasts"
                }
            ]
        },
        companyApplicantForm: {
            fields: [
                {
                    field: "APPLICANTNAME",
                    title: "Uzņēmuma nosaukums",
                    type: Input.Text,
                    autocomplete: "organization",
                    required: true
                },
                {
                    field: "APPLICANTCODE",
                    title: "Reģistrācijas numurs",
                    type: Input.Text,
                    required: true,
                    validationFn: (value): boolean => value.length === 9 || value.length === 11,
                    validationErrorMessage: "Kļūdains reģistrācijas numurs"
                },
                {
                    field: "APPLICANTADDRESS",
                    title: "Adrese",
                    type: Input.Text,
                    required: true,
                    autocomplete: "street-address",
                    validationFn: (value): boolean => value.length <= 250,
                    validationErrorMessage: "Pārsniedz 250 zīmes"
                },
                {
                    field: "APPLICANTPHONE",
                    title: "Telefons",
                    type: Input.Phone,
                    autocomplete: "tel",
                    required: true,
                    validationFn: (value): boolean => (value.match(/^\+?\d{8,11}$/) ? true : false), // only digits, 8-11 symbols
                    validationErrorMessage: "Kļūdains telefona numurs"
                },
                {
                    field: "APPLICANTEMAIL",
                    title: "E-pasts",
                    type: Input.Email,
                    autocomplete: "email",
                    required: true,
                    validationFn: (value: string, APPLICANTCODE: string): boolean => {
                        const isEmail = value.match(/^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/) ? true : false;
                        const [name, number] = value.split("@");
                        const isEadress = APPLICANTCODE
                            ? number === APPLICANTCODE && (number.length === 9 || number.length === 11)
                            : number.length === 9 || number.length === 11;
                        return isEmail || isEadress;
                    },
                    validationErrorMessage: "Kļūdains epasts",
                    validationComparator: "APPLICANTCODE"
                },
                { field: "CONTACTPERSON", title: "Kontaktpersona", type: Input.Text, required: true },
                {
                    field: "CONTACTPERSONPHONE",
                    title: "Kontaktpersonas telefons",
                    type: Input.Phone,
                    required: true,
                    validationFn: (value): boolean => (value.match(/^\+?\d{8,11}$/) ? true : false), // only digits, 8-11 symbols
                    validationErrorMessage: "Kļūdains telefona numurs"
                },
                {
                    field: "SAMECONTACTPERSONMAIL",
                    title: "Kontaktpersonas E-pasts vienāds ar iesniedzēju",
                    type: Input.Checkbox
                },
                {
                    field: "CONTACTPERSONMAIL",
                    title: "Kontaktpersonas E-pasts",
                    type: Input.Email,
                    autocomplete: "email",
                    validationFn: (value: string): boolean =>
                        value.match(/^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/) ? true : false,

                    validationErrorMessage: "Kļūdains epasts"
                }
            ]
        },
        eventForm: {
            fields: [
                { field: "EVENTTYPE", title: "Pasākuma veids", type: Input.Select, required: true },
                {
                    field: "LEARNINGTYPE",
                    title: "Mācību veids",
                    type: Input.Select,
                    required: true,
                    visibilityDeterminantField: "EVENTTYPE",
                    visibilityDeterminantFieldValue: "1"
                },
                { field: "EVENTNAME", title: "Pasākuma nosaukums", type: Input.Text, required: true },
                {
                    field: "DATEFROM",
                    title: "Norises datums no",
                    type: Input.Date,
                    required: true,
                    maxDateField: "DATETO"
                },
                {
                    field: "DATETO",
                    title: "Norises datums līdz",
                    type: Input.Date,
                    required: true,
                    minDateField: "DATEFROM"
                },
                { field: "VISITORNUMBER", title: "Apmeklētāju skaits", type: Input.Select, required: true },
                {
                    field: "ISFORESTNIGHT",
                    title: "Paredzēta nakšņošana mežā",
                    type: Input.Checkbox,
                    visibilityDeterminantField: "anything"
                },
                {
                    field: "NIGHTSCOUNT",
                    title: "Naktis, skaits",
                    type: Input.Number,
                    required: true,
                    visibilityDeterminantField: "ISFORESTNIGHT",
                    visibilityDeterminantFieldValue: true
                },
                { field: "ISROUTEMARKED", title: "Paredzēta maršruta marķēšana", type: Input.Checkbox },
                {
                    field: "ROUTEMARKERTYPE",
                    title: "Marķējuma tips",
                    type: Input.Select,
                    required: true,
                    visibilityDeterminantField: "ISROUTEMARKED",
                    visibilityDeterminantFieldValue: true
                },
                { field: "ROADCLOSURE", title: "Pasākuma norisei nepieciešama ceļu slēgšana", type: Input.Checkbox },
                {
                    field: "ADDITIONALINFO",
                    title: "Cita būtiska informācija",
                    type: Input.TextArea,
                    maxlength: 250,
                    requiredDeterminantField: "EVENTTYPE",
                    requiredDeterminantFieldValue: "1"
                },
                { field: "", title: "Par drošību un kārtību atbildīgā organizatora kontaktpersona", type: Input.Label },
                {
                    field: "SAMEPERSONRESPONSIBLE",
                    title: "Vienāds ar iesniedzēju",
                    type: Input.Checkbox
                },
                { field: "RESPONSIBLEPERSON", title: "Vārds, uzvārds", type: Input.Text, required: true },
                {
                    field: "RESPONSIBLEPERSONPHONE",
                    title: "Telefona numurs",
                    type: Input.Phone,
                    required: true,
                    autocomplete: "tel",
                    validationFn: (value): boolean => (value.match(/^\+?\d{8,11}$/) ? true : false), // only digits, 8-11 symbols
                    validationErrorMessage: "Kļūdains telefona numurs"
                }
            ]
        }
    },
    search: {
        services: [
            {
                id: "adresses",
                url: proxyUrl + "proxy/b3bab3192ed5477d8854f4eea8601f7f",
                searchLayers: [
                    { layerId: "0", field: "MUNICIPALITYNAME" },
                    { layerId: "1", field: "REGIONNAME" }
                ]
            },
            {
                id: "cadaster",
                url: proxyUrl + "proxy/465e96b5d57c4f9c845fae5ebda8305b",
                searchLayers: [{ layerId: "1", field: "CODE" }]
            }
        ]
    },
    reCaptchaSiteKey: "6Ld_N5seAAAAAPVbnoGMwILxB_hVXPY32mu90RpQ",
    reCaptchaErrorMessage:
        "Drošības kļūda apstrādājot pieprasījumu. Lūdzam atjaunot pārlūka versiju vai izvēlēties citu pārlūkprogrammu.",
    upload: {
        url: proxyUrl + "proxy/fp_save_67312ef2b5244f28a6183e2397755e88/api/Save",
        fieldName: "FILEID",
        layerName: "PUBSDE.LVMFormNIPublicEventsFiles"
    },
    print: {
        endpoint: "/print",
        url: proxyUrl + "proxy/print_846d8f58c3da4f91bc914f7fca62d0ec"
    },
    email: {
        subject: "Paziņojums par AS “Latvijas valsts meži” iesniegtu pieteikumu",
        body: "AS “Latvijas valsts meži” ir saņemts Jūsu iesniegtais pieteikums par aktivitātes vai pasākuma saskaņošanu. Par saskaņojumu pasākuma norisei ar Jums sazināsies attiecīgā reģiona Nekustamo īpašumu pārvaldes Zemes lietojuma speciālists.",
        url: proxyUrl + "proxy/email_4f7ad5fa1c62484cab58d7d64013f4aa/api/send"
    },
    geometryService: {
        endpoint: "/api/geometryservice/intersect",
        url: proxyUrl + "proxy/geometry_1daf82b0fba14e20935e972913ff683b"
    }
};
